import React, { useMemo } from 'react';
import {
  BookmarkBorder,
  BookmarkOutlined,
  ChatBubbleOutline,
  GroupsOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Card,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { JunoUser, RoleEnum, UserSettings } from '@juno/client-api/model';
import { CallButton, WithOptionalTooltip } from '@juno/ui';
import { FEATURE_FLAG, useBreakpoint, useFeatureFlagWithUser, useSettings } from '@juno/utils';

interface ExtendedUser extends JunoUser {
  settings: UserSettings;
}
interface DirectoryTileProps {
  user: JunoUser;
  selectUser: (user: JunoUser, e: React.MouseEvent) => void;
  startMeeting: (user: JunoUser) => void;
  startChat: (user: JunoUser) => void;
  saveUser: (user: JunoUser) => void;
  isSaved?: boolean;
  messengerDisabled?: boolean;
  showSaveButton?: boolean;
}
const DirectoryTile: React.FC<DirectoryTileProps> = ({
  user,
  selectUser,
  startMeeting,
  startChat,
  saveUser,
  isSaved = false,
  messengerDisabled,
  showSaveButton = false,
}) => {
  const { user: currentUser, featureConfigs, isStaff } = useSettings();
  const { enablePrivateMeetings } = featureConfigs?.user_and_profile || {};
  const { enableMessenger } = featureConfigs?.user_and_profile || {};
  const { value: privateMeetingsValue } = useFeatureFlagWithUser(FEATURE_FLAG.PRIVATE_MEETINGS);
  const { xs } = useBreakpoint();
  const isCurrentUser = user.id === currentUser?.id;
  const userName = isCurrentUser
    ? `${user.first_name} ${user.last_name} (You)`
    : `${user.first_name} ${user.last_name}`;

  const numSharedGroups = useMemo(() => {
    if (isCurrentUser) return 0;
    const myGroups = currentUser?.community_groups_member || [];
    const theirGroups = user.community_groups_member || [];
    return myGroups.filter((g) => theirGroups.includes(g)).length;
  }, [user, currentUser]);

  return (
    <Card sx={{ px: 2, width: '100%', height: 96, display: 'flex', alignItems: 'center' }}>
      <Grid container>
        <Grid item xs={xs ? 6 : 8}>
          {user.active ? (
            <Box display={'flex'} onClick={(e) => selectUser(user, e)} sx={{ cursor: 'pointer' }}>
              <Avatar
                alt={userName}
                src={user.avatar || ''}
                sx={{ width: 62, height: 62, mr: 2 }}
              />
              <Stack justifyContent={'center'}>
                <Typography variant='subtitle1'>{user.first_name}</Typography>
                <Typography variant='subtitle1'>{user.last_name}</Typography>
                {user.company && <Typography variant='subtitle2'>{user.company}</Typography>}
              </Stack>
            </Box>
          ) : (
            <Tooltip title='Deactivated User' placement='top-start'>
              <Box display={'flex'} sx={{ cursor: 'not-allowed', opacity: '0.3' }}>
                <Avatar
                  alt={userName}
                  src={user.avatar || ''}
                  sx={{ width: 56, height: 56, mr: 2 }}
                />
                <Box>
                  <Typography>{userName}</Typography>
                  <Typography variant='subtitle2'>{user.company}</Typography>
                </Box>
              </Box>
            </Tooltip>
          )}
        </Grid>

        {user.active && (
          <Grid
            item
            xs={xs ? 6 : 4}
            alignItems={'center'}
            display={'flex'}
            justifyContent={'flex-end'}
          >
            {numSharedGroups > 0 && (
              <Tooltip title={`${numSharedGroups} shared group${numSharedGroups > 1 ? 's' : ''}`}>
                <Box mr={1} position={'relative'} pt={0.5}>
                  <GroupsOutlined sx={{ fontSize: '28px', color: '#525252' }} />
                  <Badge
                    badgeContent={numSharedGroups}
                    color='primary'
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 0,
                      '.MuiBadge-badge': { height: 14, minWidth: 14, borderRadius: 1.25 },
                    }}
                  />
                </Box>
              </Tooltip>
            )}
            {enablePrivateMeetings && privateMeetingsValue && <CallButton otherUser={user} />}
            {enableMessenger && !messengerDisabled && (
              <StartChatButton
                user={user}
                isCurrentUser={isCurrentUser}
                startChat={startChat}
                isStaff={isStaff}
              />
            )}
            {showSaveButton && (
              <SaveUserButton
                user={user}
                isSaved={isSaved}
                saveUser={saveUser}
                isCurrentUser={isCurrentUser}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default DirectoryTile;

const SaveUserButton = ({
  user,
  isSaved,
  saveUser,
  isCurrentUser,
}: {
  user: JunoUser;
  isSaved: boolean;
  saveUser: (user: JunoUser) => void;
  isCurrentUser: boolean;
}) => {
  const showTooltip = !isCurrentUser;

  return (
    <WithOptionalTooltip show={showTooltip} title={isSaved ? 'Remove contact' : 'Save contact'}>
      <IconButton
        aria-label='save-or-remove-user'
        disabled={isCurrentUser}
        onClick={() => saveUser(user)}
      >
        {isSaved ? (
          <BookmarkOutlined color='primary' sx={{ fontSize: '24px' }} />
        ) : (
          <BookmarkBorder sx={{ fontSize: '24px' }} />
        )}
      </IconButton>
    </WithOptionalTooltip>
  );
};

const StartChatButton = ({
  user,
  isCurrentUser,
  startChat,
  isStaff,
}: {
  user: JunoUser;
  isCurrentUser: boolean;
  startChat: (user: JunoUser) => void;
  isStaff: boolean | undefined;
}) => {
  const privateMessagingOn =
    (user as ExtendedUser).settings?.disabled_private_messaging != undefined
      ? !(user as ExtendedUser).settings?.disabled_private_messaging
      : false;
  const showTooltip = !isCurrentUser && privateMessagingOn;
  const isDisabled =
    isCurrentUser || !privateMessagingOn || isStaff || user.role === RoleEnum.NUMBER_2;

  return (
    <WithOptionalTooltip show={showTooltip} title='Start a chat'>
      <IconButton aria-label='start-chat' disabled={isDisabled} onClick={() => startChat(user)}>
        <ChatBubbleOutline sx={{ fontSize: '24px' }} />
      </IconButton>
    </WithOptionalTooltip>
  );
};

export const SkeletonDirectoryTile = () => {
  return (
    <Card sx={{ px: 2, height: 96, mb: 2 }}>
      <Stack direction='row' alignItems={'center'} justifyContent={'space-between'} height={'100%'}>
        <Stack direction='row'>
          <Skeleton variant='circular' height={74} width={74} />
          <Stack justifyContent={'center'} ml={1} spacing={1}>
            <Skeleton variant='rounded' height={16} width={60} />
            <Skeleton variant='rounded' height={16} width={85} />
            <Skeleton variant='rounded' height={10} width={124} />
          </Stack>
        </Stack>
        <Stack direction='row' spacing={2} mr={1}>
          <Skeleton variant='circular' height={20} width={20} />
          <Skeleton variant='circular' height={20} width={20} />
          <Skeleton variant='circular' height={20} width={20} />
        </Stack>
      </Stack>
    </Card>
  );
};
